import type { BulkAclEntity, MarketViewListEntry } from "@eu.mdotm.api/sphere";
import { useTypedNavigation } from "$root/components/PlatformRouter/RoutesDef";
import { spawnYesNoDialog } from "$root/components/spawnable/yes-no-dialog";
import { roleByArea } from "$root/functional-areas/acl/checkers/all";
import { validateACLPermissions } from "$root/functional-areas/acl/checkers/shared";
import { useUpdateAccessListControlDialog } from "$root/functional-areas/acl/hook/useUpdateAccessListControlDialog";
import {
	checkBulkActionAvailability,
	BulkActionReviewSections,
	spawnBulkActionReviewDialog,
} from "$root/functional-areas/portfolio-studio/bulk-actions/common";
import { MarketViewActionUnavailableReason } from "$root/functional-areas/portfolio-studio/bulk-actions/market-view-actions";
import { marketViewListActionValidators } from "$root/functional-areas/portfolio-studio/bulk-actions/market-view-actions/validators";
import { marketViewReasonToMessageMap } from "$root/functional-areas/portfolio-studio/bulk-actions/market-view-actions/reason-messages";
import { spawnBulkShareAction } from "$root/functional-areas/portfolio-studio/bulk-actions/spawn-bulk-share-action";
import { marketViewEntityManagementActions } from "$root/functional-areas/market-view/entity-management";
import { useUserValue } from "$root/functional-areas/user";
import { persistedColStateKeys } from "$root/functional-areas/user/userStorage";
import { AgGridWithModules, useAgGridSelectedNodes } from "$root/third-party-integrations/ag-grid/ag-grid";
import { columnsDefinition } from "$root/ui-lib/table/column";
import { agExtra } from "$root/ui-lib/table/default-configuration";
import { parallelize, preventSubmitOnPressEnter } from "$root/utils";
import { Icon } from "@mdotm/shadcn/legacy/components";
import { TextInput, BulkActions } from "@mdotm/shadcn/mdotm/components";
import { useDebouncedMemo } from "@mdotm/shadcn/legacy/react-extensions";
import { noop, unpromisify } from "@mdotm/shadcn/legacy/utils";
import type { GetContextMenuItems } from "ag-grid-enterprise";
import type { AgGridReact } from "ag-grid-react";
import { type FC, useCallback, useMemo, useState } from "react";
import { renderToStaticMarkup } from "react-dom/server";
import { useTranslation } from "react-i18next";
import { multipleEntityDelete, notifyUser } from "../common";
import { marketViewRepository } from "$root/functional-areas/market-view/repository";
import { runWithErrorReporting } from "$root/api/error-reporting";

type MarketViewsListProps = {
	markets: MarketViewListEntry[];
};

const MarketViewsList: FC<MarketViewsListProps> = ({ markets }) => {
	const [searchQuery, setSearchQuery] = useState("");
	const [gridEl, setGridEl] = useState<AgGridReact<MarketViewListEntry> | null>(null);

	const { aclSpawn } = useUpdateAccessListControlDialog("MARKET_VIEW");
	const { t } = useTranslation();
	const { push } = useTypedNavigation();
	const selectedNodes = useAgGridSelectedNodes(gridEl);

	const user = useUserValue();

	const { value: debouncedLowerCaseSearchQuery } = useDebouncedMemo(
		() => searchQuery.toLocaleLowerCase(),
		[searchQuery],
		{
			debounceInterval: 200,
		},
	);

	const filteredRows = useMemo(
		() =>
			markets.filter((item) =>
				[item.name ?? "", item.forecastHorizon ?? ""].some((el) =>
					el.toLowerCase().includes(debouncedLowerCaseSearchQuery),
				),
			),
		[debouncedLowerCaseSearchQuery, markets],
	);

	const getContextMenuItems = useCallback<GetContextMenuItems<MarketViewListEntry, any>>(
		(row) => {
			const marketView = row.node?.data;
			if (!marketView) {
				return ["copy"];
			}
			const entityManagementActions = marketViewEntityManagementActions(user, marketView);

			return [
				"copy",
				{
					disabled: !entityManagementActions.deleteAsync,
					icon: renderToStaticMarkup(<Icon icon="Delete" size={16} />),
					action: unpromisify(entityManagementActions.deleteAsync || noop),
					name: "Delete",
				},
				{
					disabled: !entityManagementActions.renameAsync,
					icon: renderToStaticMarkup(<Icon icon="Edit" size={16} />),
					action: unpromisify(entityManagementActions.renameAsync || noop),
					name: "Rename",
				},
				{
					disabled: !entityManagementActions.duplicateAsync,
					icon: renderToStaticMarkup(<Icon icon="Content-Copy" size={16} />),
					action: () => push("MarketViewWorkSpace/Clone", { uuid: marketView.uuid! }),
					name: "Duplicate",
				},
				{
					disabled: !validateACLPermissions(user.id, marketView.richAcl?.acl ?? [], roleByArea.marketView.EDITOR),
					icon: renderToStaticMarkup(<Icon icon="share" size={16} />),
					action: unpromisify(() => aclSpawn(marketView.name, marketView.uuid)),
					name: "Share",
				},
			];
		},
		[user, push, aclSpawn],
	);

	const columns = columnsDefinition.useMarketViewList();

	return (
		<>
			<div className="min-h-0 min-w-0 flex flex-col flex-1 basis-0! rounded-lg bg-white px-4 py-6">
				<TextInput
					value={searchQuery}
					name="search"
					maxLength={60}
					onChangeText={setSearchQuery}
					placeholder={t("PORTFOLIOS.CONSTRAINTS_TARGETS.PORTFOLIO_LIST")}
					onKeyDown={preventSubmitOnPressEnter}
					style={{ width: 692 }}
					leftContent={<Icon icon="Search" size="1.4em" />}
					className="mb-4"
				/>

				<AgGridWithModules<MarketViewListEntry>
					{...agExtra.checkboxColumn({
						mode: "multiRow",
						selectAll: "currentPage",
					})}
					{...agExtra.usePersistColState(persistedColStateKeys["tableV3-marketViewList"])}
					innerRef={setGridEl}
					getContextMenuItems={getContextMenuItems}
					columnDefs={columns}
					rowData={filteredRows}
				/>
			</div>

			<BulkActions
				variant="floating"
				count={selectedNodes.length}
				onClear={() => gridEl?.api.deselectAll()}
				actions={[
					{
						label: "Delete",
						icon: "Delete",
						onClick() {
							const reasonKeys = Object.values(MarketViewActionUnavailableReason);
							const availabilityResult = checkBulkActionAvailability(
								reasonKeys,
								marketViewListActionValidators.delete,
								selectedNodes,
							);
							const [available, unavailable] = availabilityResult;

							async function onContinue() {
								const response = await multipleEntityDelete({ area: "MarketViews", items: available });
								gridEl?.api.deselectAll();
								notifyUser({ t, succeded: response.succeded, area: "MarketViews" });
							}

							if (unavailable === null) {
								spawnYesNoDialog({
									size: "large",
									header: t("PORTFOLIOS.MODAL.BULK_DELETE.TITLE"),
									yesButton: t("BUTTON.DELETE"),
									noButton: t("BUTTON.CANCEL"),
									children: t("PORTFOLIOS.MODAL.BULK_DELETE.DESCRIPTION", {
										size: available.length,
									}),
									onSubmitAsync: onContinue,
								})
									.then(noop)
									.catch(noop);
							} else {
								spawnBulkActionReviewDialog({
									initialState: null,
									header: "Delete: review and confirm",
									children: (
										<BulkActionReviewSections
											availabilityResult={availabilityResult}
											reasonToMessage={marketViewReasonToMessageMap}
											getRedirectUrl={() => ""}
											hideRedirectUrl
											showEntityNames
											labels={{ entitySingular: "Market view", entityPlural: "Market views" }}
										/>
									),
									canConfirm: available.length > 0,
									onContinue,
								}).catch(noop);
							}
						},
						"data-qualifier": "MarketViewList/BatchAction(Delete)",
					},
					{
						label: "Share",
						icon: "share",
						async onClickAsync() {
							runWithErrorReporting(
								async () => {
									await spawnBulkShareAction({
										reasonKeys: Object.values(MarketViewActionUnavailableReason),
										validator: marketViewListActionValidators.share,
										entities: selectedNodes,
										reasonToMessage: marketViewReasonToMessageMap,
										labels: { entitySingular: "Market view", entityPlural: "Market views" },
										getRedirectUrl: () => "",
										hideRedirectUrl: true,
										showEntityNames: true,
										area: "marketView",
										customerAndLinkedAclProvider: (available) => () =>
											parallelize(
												available.map((marketView) => () => marketViewRepository.getACL.fetchQuery(marketView.uuid!)),
											),
										getEntityMetadata: (marketView) => ({
											uuid: marketView.uuid!,
											name: marketView.name!,
											entityType: "MARKET_VIEW" as const,
										}),
										async saveAclHandler(available, { aclByEntity }) {
											const marketViewsToShare = available.map(
												(marketView): BulkAclEntity => ({
													accessControlList: aclByEntity[marketView.uuid!],
													entityUuid: marketView.uuid!,
												}),
											);
											await marketViewRepository.bulkShare(marketViewsToShare);
										},
									});
								},
								{ area: "alerts", attemptedOperation: { message: "Bulk action entities error" } },
							);
						},
						"data-qualifier": "MarketViewList/BatchAction(Share)",
					},
				]}
			/>
		</>
	);
};

export default MarketViewsList;
